import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  findMasterBrandCategory,
  getActiveBrandCategories,
} from '../mappers/brandCategoryMapper'
import { getActiveDocumentTypes } from '../mappers/documentTypeMapper'
import { brandService as sharedBrandService } from '../services/brandService'

const BrandContext = createContext(null)

export const BrandProvider = ({ brandService: service, children }) => {
  const brandService = service || sharedBrandService
  const [allCategories, setAllCategories] = useState([])
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [allDocumentTypes, setAllDocumentTypes] = useState([])
  const [documentTypeError, setDocumentTypeError] = useState(null)
  const [isDocumentTypesLoading, setIsDocumentTypesLoading] = useState(true)
  const [allSkillInstructions, setAllSkillInstructions] = useState([])
  const [skillInstructionError, setSkillInstructionError] = useState(null)
  const [isSkillInstructionsLoading, setIsSkillInstructionsLoading] =
    useState(true)

  const loadCategories = useCallback(async () => {
    setError(null)
    setIsLoading(true)
    try {
      const response = await brandService.getAllBrandCategories()
      setAllCategories(response)
      return response
    } catch (requestError) {
      setError(requestError)
      throw requestError
    } finally {
      setIsLoading(false)
    }
  }, [brandService])

  useEffect(() => {
    loadCategories().catch(() => {})
  }, [loadCategories])

  const createBrandCategory = useCallback(
    async (category) => {
      const createdCategory = await brandService.createBrandCategory(category)
      await loadCategories()
      return createdCategory
    },
    [brandService, loadCategories],
  )

  const updateBrandCategory = useCallback(
    async (id, category) => {
      const updatedCategory = await brandService.updateBrandCategory(
        id,
        category,
      )
      await loadCategories()
      return updatedCategory
    },
    [brandService, loadCategories],
  )

  const loadDocumentTypes = useCallback(async () => {
    setDocumentTypeError(null)
    setIsDocumentTypesLoading(true)
    try {
      const response = await brandService.getAllDocumentTypes()
      setAllDocumentTypes(response)
      return response
    } catch (requestError) {
      setDocumentTypeError(requestError)
      throw requestError
    } finally {
      setIsDocumentTypesLoading(false)
    }
  }, [brandService])

  useEffect(() => {
    loadDocumentTypes().catch(() => {})
  }, [loadDocumentTypes])

  const createDocumentType = useCallback(
    async (documentType) => {
      const createdDocumentType =
        await brandService.createDocumentType(documentType)
      await loadDocumentTypes()
      return createdDocumentType
    },
    [brandService, loadDocumentTypes],
  )

  const updateDocumentType = useCallback(
    async (id, documentType) => {
      const updatedDocumentType = await brandService.updateDocumentType(
        id,
        documentType,
      )
      await loadDocumentTypes()
      return updatedDocumentType
    },
    [brandService, loadDocumentTypes],
  )

  const loadSkillInstructions = useCallback(async () => {
    setSkillInstructionError(null)
    setIsSkillInstructionsLoading(true)
    try {
      const response = await brandService.getAllPromptModes()
      setAllSkillInstructions(response)
      return response
    } catch (requestError) {
      setSkillInstructionError(requestError)
      throw requestError
    } finally {
      setIsSkillInstructionsLoading(false)
    }
  }, [brandService])

  useEffect(() => {
    loadSkillInstructions().catch(() => {})
  }, [loadSkillInstructions])

  const createSkillInstruction = useCallback(
    async (skillInstruction) => {
      const createdSkillInstruction =
        await brandService.createPromptMode(skillInstruction)
      await loadSkillInstructions()
      return createdSkillInstruction
    },
    [brandService, loadSkillInstructions],
  )

  const getSkillInstructionVersions = useCallback(
    async (skillCode) => brandService.getPromptModeVersions(skillCode),
    [brandService],
  )

  const saveSkillInstructionDraftVersion = useCallback(
    async (skillCode, versionDraft, promptVersionId) =>
      brandService.savePromptModeDraftVersion(
        skillCode,
        versionDraft,
        promptVersionId,
      ),
    [brandService],
  )

  const publishSkillInstructionVersion = useCallback(
    async (promptVersionId) =>
      brandService.publishPromptModeVersion(promptVersionId),
    [brandService],
  )

  const rollbackSkillInstructionVersion = useCallback(
    async (promptVersionId) =>
      brandService.rollbackPromptModeVersion(promptVersionId),
    [brandService],
  )

  const updateSkillInstruction = useCallback(
    async (id, skillInstruction) => {
      const updatedSkillInstruction = await brandService.updatePromptMode(
        id,
        skillInstruction,
      )
      await loadSkillInstructions()
      return updatedSkillInstruction
    },
    [brandService, loadSkillInstructions],
  )

  const value = useMemo(() => {
    const activeCategories = getActiveBrandCategories(allCategories)
    const categoriesById = new Map(
      allCategories.map((category) => [category.id, category]),
    )
    const documentTypesById = new Map(
      allDocumentTypes.map((documentType) => [documentType.id, documentType]),
    )

    return {
      activeCategories,
      activeDocumentTypes: getActiveDocumentTypes(allDocumentTypes),
      allCategories,
      allDocumentTypes,
      allSkillInstructions,
      createBrandCategory,
      createDocumentType,
      createSkillInstruction,
      documentTypeError,
      error,
      getCategoryById: (id) => categoriesById.get(id) || null,
      getDocumentTypeById: (id) => documentTypesById.get(id) || null,
      getSkillInstructionVersions,
      isDocumentTypesLoading,
      isLoading,
      isSkillInstructionsLoading,
      masterBrandCategory: findMasterBrandCategory(activeCategories),
      publishSkillInstructionVersion,
      refreshCategories: loadCategories,
      refreshDocumentTypes: loadDocumentTypes,
      rollbackSkillInstructionVersion,
      saveSkillInstructionDraftVersion,
      skillInstructionError,
      updateBrandCategory,
      updateDocumentType,
      updateSkillInstruction,
    }
  }, [
    allCategories,
    allDocumentTypes,
    allSkillInstructions,
    createBrandCategory,
    createDocumentType,
    createSkillInstruction,
    documentTypeError,
    error,
    getSkillInstructionVersions,
    isDocumentTypesLoading,
    isLoading,
    isSkillInstructionsLoading,
    loadCategories,
    loadDocumentTypes,
    publishSkillInstructionVersion,
    rollbackSkillInstructionVersion,
    saveSkillInstructionDraftVersion,
    skillInstructionError,
    updateBrandCategory,
    updateDocumentType,
    updateSkillInstruction,
  ])

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export const useBrand = () => {
  const context = useContext(BrandContext)
  if (!context) {
    throw new Error('useBrand must be used within BrandProvider')
  }
  return context
}
